import { fetchTokenResolutions } from "@lib/chatApi";
import type { TokenCandidateResolution, TokenResolutions } from "@lib/types";
import { useCallback, useEffect, useState } from "react";

// PII tokens that can appear in persisted chat content (story #2324):
// candidate reference tokens (C5) and thread-stable generic labels
// ([EMAIL_1]). Server-side restoration means assistant text normally
// carries real names already — this resolver covers tool outputs in the
// debug view and any token that slipped through (fallback: literal token).
export const CANDIDATE_TOKEN_PATTERN = /\bC(\d+)\b/g;
export const GENERIC_TOKEN_PATTERN = /\[([A-Z][A-Z_]*_\d+)\]/g;

const EMPTY: TokenResolutions = { generic: {}, candidates: {} };

// One fetch per thread per page load, shared across every component that
// mounts the hook (each ChatMessage row, the debug view, ...).
const cache = new Map<string, Promise<TokenResolutions>>();

function load(threadId: string): Promise<TokenResolutions> {
  let promise = cache.get(threadId);
  if (!promise) {
    promise = fetchTokenResolutions(threadId).catch((error) => {
      cache.delete(threadId);
      throw error;
    });
    cache.set(threadId, promise);
  }
  return promise;
}

export function textContainsTokens(text: string): boolean {
  CANDIDATE_TOKEN_PATTERN.lastIndex = 0;
  GENERIC_TOKEN_PATTERN.lastIndex = 0;
  return CANDIDATE_TOKEN_PATTERN.test(text) || GENERIC_TOKEN_PATTERN.test(text);
}

// Pass `undefined` to skip fetching (e.g. when the content has no tokens).
export function useTokenResolver(threadId: string | undefined) {
  const [resolutions, setResolutions] = useState<TokenResolutions>(EMPTY);
  const [loading, setLoading] = useState(Boolean(threadId));

  useEffect(() => {
    if (!threadId) return;
    let active = true;
    setLoading(true);
    load(threadId)
      .then((data) => {
        if (active) setResolutions(data);
      })
      .catch(() => {
        if (active) setResolutions(EMPTY);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [threadId]);

  const resolveCandidate = useCallback(
    (token: string): TokenCandidateResolution | null => resolutions.candidates[token] ?? null,
    [resolutions],
  );

  const resolveGeneric = useCallback(
    (label: string): string | null => {
      const key = label.replace(/^\[|\]$/g, "");
      return resolutions.generic[key] ?? null;
    },
    [resolutions],
  );

  // Plain-text resolution: every resolvable token becomes its display
  // value; unresolvable tokens stay literal.
  const resolveText = useCallback(
    (text: string): string =>
      text
        .replace(GENERIC_TOKEN_PATTERN, (match, label) => resolutions.generic[label] ?? match)
        .replace(
          CANDIDATE_TOKEN_PATTERN,
          (match) => resolutions.candidates[match]?.full_name ?? match,
        ),
    [resolutions],
  );

  return { resolutions, loading, resolveCandidate, resolveGeneric, resolveText };
}
